/**
 * Quick Summary Component
 *
 * Compact summary of what's being scheduled for existing customer orders.
 * Shows the order type, container size, and service address in a read-only format.
 */

import { PackageIcon, RefreshCwIcon } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import type { LocationForm } from '../../schemas/order.schema';

interface QuickSummaryProps {
  orderType: 'swap' | 'pickup';
  containerSize: string;
  address: LocationForm;
}

export function QuickSummary({ orderType, containerSize, address }: QuickSummaryProps) {
  const orderTypeConfig = {
    swap: {
      icon: RefreshCwIcon,
      label: 'Container Swap',
      description: 'Swapping your',
    },
    pickup: {
      icon: PackageIcon,
      label: 'Container Pickup',
      description: 'Picking up your',
    },
  };

  const config = orderTypeConfig[orderType];
  const IconComponent = config.icon;

  // Format address for display
  const fullAddress = `${address.street}, ${address.city}, ${address.state} ${address.zipCode}`;

  return (
    <Card className="bg-gray-50 border-gray-200">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 mt-1">
            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <IconComponent className="w-5 h-5 text-primary-600" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              {config.label}
            </p>
            <p className="text-base font-semibold text-gray-900 mb-1">
              {config.description} {containerSize} container
            </p>
            <p className="text-sm text-gray-600 truncate" title={fullAddress}>
              {fullAddress}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
